(() => {
  const PROFILE_CONTENT = {
    desire: {
      title: 'Tu deseo necesita volver a tener su espacio en tu dia a dia',
      body: 'Las ganas y el interés íntimo han perdido presencia en tu día a día. Tu resultado también contempla cómo se relacionan con tu energía, tu sensibilidad íntima y la carga mental que estás sosteniendo.',
      productReason: 'Inira Libido está pensada como una rutina diaria sencilla para acompañar deseo, vitalidad, sensibilidad íntima y bienestar íntimo.'
    },
    response: {
      title: 'Tu cuerpo necesita recuperar la conexión de vuelta',
      body: 'Aunque pueda existir interés, sientes que tu cuerpo no acompaña como antes o que la sensibilidad ha cambiado. Tu resultado pide mirar el bienestar íntimo como un conjunto, sin convertirlo en una obligación.',
      productReason: 'Inira Libido acompaña deseo, vitalidad y bienestar íntimo desde una rutina diaria fácil de sostener.'
    },
    energy: {
      title: 'El cansancio está dejando poco espacio al deseo',
      body: 'La fatiga o la falta de vitalidad están interfiriendo con tu capacidad de conectar contigo. Tu resultado sugiere que recuperar energía percibida forma parte de cuidar también tu bienestar íntimo.',
      productReason: 'Inira Libido reúne una rutina integral para acompañar vitalidad, deseo y bienestar íntimo cuando el cansancio ocupa demasiado espacio.'
    },
    emotional: {
      title: 'La carga mental está dificultando que conectes contigo',
      body: 'El estrés, la preocupación o la presión están ocupando el espacio que necesita el deseo. No se trata de esforzarte más, sino de construir una rutina que contemple cómo te sientes de forma completa.',
      productReason: 'Inira Libido ofrece una rutina diaria para acompañar deseo, vitalidad y bienestar íntimo sin añadir más presión a tu día.'
    },
    integral: {
      title: 'Tu bienestar íntimo pide una mirada integral',
      body: 'Deseo, sensibilidad íntima, energía y estado emocional se están solapando. Tu resultado indica que tiene más sentido acompañar el conjunto con una rutina constante que perseguir cada factor por separado.',
      productReason: 'Inira Libido reúne una rutina diaria pensada para acompañar deseo, sensibilidad íntima, energía y conexión emocional en conjunto.'
    }
  };

  const AXIS_LABELS = {
    desire: 'Deseo',
    response: 'Conexión íntima',
    energy: 'Energía y vitalidad',
    emotional: 'Estrés y calma'
  };

  const SCREEN_FLOW = [
    'intro',
    'stage',
    'priority',
    'desire',
    'impact',
    'response',
    'energy',
    'emotional',
    'timing',
    'duration',
    'goal',
    'analysis',
    'result',
    'discount'
  ];

  const SCREEN_NEXT = Object.fromEntries(SCREEN_FLOW.map((screen, index) => [screen, SCREEN_FLOW[index + 1]]));

  class LibidoQuiz extends HTMLElement {
    constructor() {
      super();
      this.answers = {};
      this.history = [];
      this.currentScreen = 'intro';
      this.answerTimer = null;
      this.analysisFrame = null;
      this.scratchCleanup = null;
      this.discountCountdownTimer = null;
      this.redirectTimer = null;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.chrome = this.querySelector('[data-quiz-chrome]');
      this.progress = this.querySelector('[role="progressbar"]');
      this.progressValue = this.querySelector('[data-progress-value]');
      this.progressCount = this.querySelector('[data-progress-count]');
      this.progressLabel = this.querySelector('[data-progress-label]');
      this.questionActions = this.querySelector('[data-question-actions]');
      this.questionContinue = this.querySelector('[data-question-continue]');
      this.discountCode = this.dataset.discountCode || 'QUIZ10';

      this.addEventListener('click', this.handleClick.bind(this));
      this.showScreen('intro', { pushHistory: false, focus: false });
    }

    disconnectedCallback() {
      window.clearTimeout(this.answerTimer);
      this.stopAnalysis();
      this.destroyScratchCard();
      this.stopDiscountCountdown();
      window.clearTimeout(this.redirectTimer);
      this.setScrollLock(false);
    }

    handleClick(event) {
      const answer = event.target.closest('[data-answer]');
      if (answer) {
        this.selectAnswer(answer);
        return;
      }

      if (event.target.closest('[data-question-continue]')) {
        if (this.questionContinue.disabled) return;
        this.advanceFromQuestion();
        return;
      }

      const next = event.target.closest('[data-next]');
      if (next) {
        this.showScreen(next.dataset.next || SCREEN_NEXT[this.currentScreen]);
        return;
      }

      if (event.target.closest('[data-quiz-back]')) {
        this.goBack();
        return;
      }

      if (event.target.closest('[data-analysis-result]')) {
        this.stopAnalysis();
        this.renderResult();
        this.showScreen('result', { pushHistory: false });
        return;
      }

      const discountContinue = event.target.closest('[data-discount-continue]');
      if (discountContinue) {
        this.applyDiscount(discountContinue);
        return;
      }

      if (event.target.closest('[data-restart]')) {
        this.restart();
      }
    }

    selectAnswer(button) {
      const question = button.dataset.question;
      const screen = button.closest('[data-screen]');
      const previous = screen.querySelector('[data-answer].is-selected');

      if (previous) {
        previous.classList.remove('is-selected');
        previous.setAttribute('aria-pressed', 'false');
      }

      button.classList.add('is-selected');
      button.setAttribute('aria-pressed', 'true');
      this.answers[question] = {
        value: button.dataset.value,
        axis: button.dataset.axis || '',
        score: Number(button.dataset.score || 0)
      };
      this.questionContinue.disabled = false;
    }

    advanceFromQuestion() {
      const nextScreen = this.getNextScreen(this.currentScreen);
      if (nextScreen === 'analysis') {
        this.showScreen('analysis');
        this.startAnalysis();
        this.emitEvent('QuizCompleted');
        return;
      }
      this.showScreen(nextScreen);
    }

    getNextScreen(screen) {
      return SCREEN_NEXT[screen] || 'result';
    }

    showScreen(screenName, options = {}) {
      const { pushHistory = true, focus = true } = options;
      const nextScreen = this.querySelector(`[data-screen="${screenName}"]`);
      if (!nextScreen) return;

      const active = this.querySelector('[data-screen].is-active');
      if (pushHistory && active && active.dataset.screen !== screenName) {
        this.history.push(active.dataset.screen);
      }

      this.querySelectorAll('[data-screen]').forEach((screen) => {
        screen.classList.remove('is-active', 'is-entering');
        screen.hidden = true;
      });

      nextScreen.hidden = false;
      nextScreen.classList.add('is-active', 'is-entering');
      this.currentScreen = screenName;
      const isDiscount = screenName === 'discount';
      const isResult = screenName === 'result';
      const isAnalysis = screenName === 'analysis';
      this.classList.toggle('is-analysis-screen', isAnalysis);
      this.classList.toggle('is-discount-screen', isDiscount);
      this.classList.toggle('is-result-screen', isResult);
      if (isDiscount) {
        this.startDiscountCountdown();
      } else {
        this.stopDiscountCountdown();
      }
      this.setScrollLock(isAnalysis || isDiscount || isResult);
      this.updateChrome(nextScreen);
      this.updateQuestionActions(nextScreen);

      if (screenName === 'stage') this.emitEvent('QuizStarted');
      if (screenName === 'result') this.emitEvent('RecommendationViewed');
      if (isDiscount) {
        window.requestAnimationFrame(() => this.initializeScratchCard());
      } else {
        this.destroyScratchCard();
      }

      if (focus) {
        const heading = nextScreen.querySelector('h1, h2');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
        this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    }

    updateChrome(screen) {
      const hideChrome = ['intro', 'analysis', 'discount', 'result'].includes(screen.dataset.screen);
      this.chrome.hidden = hideChrome;
      if (hideChrome) return;

      const questionNumber = Number(screen.dataset.questionNumber || this.getCompletedQuestionCount());
      const displayNumber = Math.min(questionNumber, 10);
      const progressPercent = Math.max(0, Math.min(100, (displayNumber / 10) * 100));

      this.progress.setAttribute('aria-valuenow', String(displayNumber));
      this.progressValue.style.width = `${progressPercent}%`;
      if (this.progressCount) this.progressCount.textContent = `${displayNumber} / 10`;
      if (this.progressLabel) this.progressLabel.textContent = this.getProgressLabel(displayNumber);
    }

    updateQuestionActions(screen) {
      const isQuestion = screen.hasAttribute('data-question-screen');
      this.questionActions.hidden = !isQuestion;
      if (!isQuestion) return;

      const question = screen.querySelector('[data-answer]')?.dataset.question;
      this.questionContinue.disabled = !question || !this.answers[question];
    }

    getProgressLabel(questionNumber) {
      if (questionNumber <= 3) return 'Tu momento';
      if (questionNumber <= 5) return 'Tu rutina';
      if (questionNumber <= 8) return 'Tu bienestar';
      return 'Tu guía';
    }

    getCompletedQuestionCount() {
      return Object.keys(this.answers).length;
    }

    goBack() {
      window.clearTimeout(this.answerTimer);
      this.stopAnalysis();
      const previous = this.history.pop();
      if (!previous) return;
      this.showScreen(previous, { pushHistory: false });
    }

    calculateScores() {
      const scores = { desire: 0, response: 0, energy: 0, emotional: 0 };

      Object.values(this.answers).forEach((answer) => {
        if (answer.axis && Object.prototype.hasOwnProperty.call(scores, answer.axis)) {
          scores[answer.axis] += answer.score;
        }
        if (answer.axis === 'integral') {
          Object.keys(scores).forEach((axis) => {
            scores[axis] += answer.score;
          });
        }
      });

      return scores;
    }

    getPrimaryAxis(scores) {
      const highAxes = Object.values(scores).filter((score) => score >= 4).length;
      if (this.answers.desire?.value === 'integral' || this.answers.goal?.value === 'integral' || highAxes >= 3) return 'integral';

      return Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] || 'integral';
    }

    getAxisLevel(score, axis) {
      if (score >= 6) return 'Prioridad alta';
      if (axis === 'desire') return 'Prioridad media';
      if (score >= 3) return 'Prioridad media';
      return 'Prioridad baja';
    }

    renderResult() {
      const scores = this.calculateScores();
      const profile = this.getPrimaryAxis(scores);
      const content = PROFILE_CONTENT[profile];

      this.querySelector('[data-result-title]').textContent = content.title;
      this.querySelector('[data-result-body]').textContent = content.body;
      this.querySelector('[data-product-reason]').textContent = content.productReason;

      Object.entries(scores).forEach(([axis, score]) => {
        const level = this.getAxisLevel(score, axis);
        const bar = this.querySelector(`[data-result-area-bar="${axis}"]`);
        const label = this.querySelector(`[data-result-area-level="${axis}"]`);
        if (bar) bar.style.width = `${Math.max(38, Math.min(100, 38 + score * 5.2))}%`;
        if (label) label.textContent = level;
      });
    }

    startAnalysis() {
      this.stopAnalysis();

      const progress = this.querySelector('[data-analysis-progress]');
      const progressValue = this.querySelector('[data-analysis-progress-value]');
      const percent = this.querySelector('[data-analysis-percent]');
      const steps = Array.from(this.querySelectorAll('[data-analysis-step]'));
      const resultButton = this.querySelector('[data-analysis-result]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = reducedMotion ? 900 : 6500;
      const startedAt = performance.now();

      progress.setAttribute('aria-valuenow', '0');
      progressValue.style.width = '0%';
      percent.textContent = '0%';
      resultButton.hidden = true;
      steps.forEach((step) => step.classList.remove('is-active', 'is-complete'));
      steps[0]?.classList.add('is-active');

      const update = (now) => {
        if (this.currentScreen !== 'analysis') return;

        const elapsed = Math.min(duration, now - startedAt);
        const value = Math.min(100, Math.round((elapsed / duration) * 100));
        const activeIndex = Math.min(steps.length - 1, Math.floor((value / 100) * steps.length));

        progress.setAttribute('aria-valuenow', String(value));
        progressValue.style.width = `${value}%`;
        percent.textContent = `${value}%`;

        steps.forEach((step, index) => {
          step.classList.toggle('is-complete', index < activeIndex || value === 100);
          step.classList.toggle('is-active', index === activeIndex && value < 100);
        });

        if (value < 100) {
          this.analysisFrame = window.requestAnimationFrame(update);
          return;
        }

        this.analysisFrame = null;
        resultButton.hidden = false;
        resultButton.focus({ preventScroll: true });
      };

      this.analysisFrame = window.requestAnimationFrame(update);
    }

    stopAnalysis() {
      if (this.analysisFrame !== null) window.cancelAnimationFrame(this.analysisFrame);
      this.analysisFrame = null;
    }

    setScrollLock(locked) {
      document.documentElement.classList.toggle('mq-lock-scroll', locked);
      document.body.classList.toggle('mq-lock-scroll', locked);
    }

    initializeScratchCard() {
      this.destroyScratchCard();

      const card = this.querySelector('[data-scratch-card]');
      const canvas = this.querySelector('[data-scratch-canvas]');
      const hint = this.querySelector('[data-scratch-hint]');
      const continueButton = this.querySelector('[data-discount-continue]');
      if (!card || !canvas || !hint || !continueButton) return;

      const context = canvas.getContext('2d', { willReadFrequently: false });
      if (!context) {
        return;
      }

      let isDrawing = false;
      let isRevealed = false;
      let lastPoint = null;
      const coveredCells = new Set();
      const gridSize = 18;
      const revealThreshold = 0.4;

      const resizeCanvas = () => {
        if (isRevealed) return;
        const rect = card.getBoundingClientRect();
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.max(1, Math.round(rect.width * ratio));
        canvas.height = Math.max(1, Math.round(rect.height * ratio));
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        coveredCells.clear();

        const gradient = context.createLinearGradient(0, 0, rect.width, rect.height);
        gradient.addColorStop(0, '#ffe47a');
        gradient.addColorStop(0.52, '#ffc914');
        gradient.addColorStop(1, '#f2b700');
        context.globalCompositeOperation = 'source-over';
        context.fillStyle = gradient;
        context.fillRect(0, 0, rect.width, rect.height);
        context.fillStyle = 'rgba(255, 255, 255, 0.2)';
        for (let index = 0; index < 24; index += 1) {
          const x = (((index * 47) % 97) / 100) * rect.width;
          const y = (((index * 71) % 89) / 92) * rect.height;
          const radius = 0.7 + (index % 3) * 0.45;
          context.beginPath();
          context.arc(x, y, radius, 0, Math.PI * 2);
          context.fill();
        }
      };

      const reveal = () => {
        if (isRevealed) return;
        isRevealed = true;
        isDrawing = false;
        canvas.classList.add('is-revealed');
        hint.hidden = true;
        continueButton.disabled = false;
        this.emitEvent('QuizDiscountRevealed');
      };

      const getPoint = (event) => {
        const rect = canvas.getBoundingClientRect();
        return {
          x: Math.max(0, Math.min(rect.width, event.clientX - rect.left)),
          y: Math.max(0, Math.min(rect.height, event.clientY - rect.top)),
          width: rect.width,
          height: rect.height
        };
      };

      const markCoverage = (point, radius) => {
        const startX = Math.max(0, Math.floor((point.x - radius) / gridSize));
        const endX = Math.ceil((point.x + radius) / gridSize);
        const startY = Math.max(0, Math.floor((point.y - radius) / gridSize));
        const endY = Math.ceil((point.y + radius) / gridSize);

        for (let y = startY; y <= endY; y += 1) {
          for (let x = startX; x <= endX; x += 1) {
            const cellX = x * gridSize + gridSize / 2;
            const cellY = y * gridSize + gridSize / 2;
            if (Math.hypot(cellX - point.x, cellY - point.y) <= radius) {
              coveredCells.add(`${x}:${y}`);
            }
          }
        }

        const totalCells = Math.ceil(point.width / gridSize) * Math.ceil(point.height / gridSize);
        if (coveredCells.size / totalCells >= revealThreshold) reveal();
      };

      const scratch = (point) => {
        const radius = Math.max(22, Math.min(34, point.width * 0.075));
        context.globalCompositeOperation = 'destination-out';
        context.lineWidth = radius * 2;
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.beginPath();
        context.moveTo(lastPoint?.x ?? point.x, lastPoint?.y ?? point.y);
        context.lineTo(point.x, point.y);
        context.stroke();
        context.beginPath();
        context.arc(point.x, point.y, radius, 0, Math.PI * 2);
        context.fill();
        markCoverage(point, radius);
        lastPoint = point;
      };

      const onPointerDown = (event) => {
        if (isRevealed) return;
        isDrawing = true;
        lastPoint = null;
        canvas.setPointerCapture(event.pointerId);
        scratch(getPoint(event));
      };

      const onPointerMove = (event) => {
        if (!isDrawing || isRevealed) return;
        scratch(getPoint(event));
      };

      const stopDrawing = (event) => {
        isDrawing = false;
        lastPoint = null;
        if (event?.pointerId !== undefined && canvas.hasPointerCapture(event.pointerId)) {
          canvas.releasePointerCapture(event.pointerId);
        }
      };

      const onKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        reveal();
      };

      const resizeObserver = new ResizeObserver(resizeCanvas);
      resizeObserver.observe(card);
      canvas.addEventListener('pointerdown', onPointerDown);
      canvas.addEventListener('pointermove', onPointerMove);
      canvas.addEventListener('pointerup', stopDrawing);
      canvas.addEventListener('pointercancel', stopDrawing);
      canvas.addEventListener('keydown', onKeyDown);
      resizeCanvas();

      this.scratchCleanup = () => {
        resizeObserver.disconnect();
        canvas.removeEventListener('pointerdown', onPointerDown);
        canvas.removeEventListener('pointermove', onPointerMove);
        canvas.removeEventListener('pointerup', stopDrawing);
        canvas.removeEventListener('pointercancel', stopDrawing);
        canvas.removeEventListener('keydown', onKeyDown);
      };
    }

    destroyScratchCard() {
      this.scratchCleanup?.();
      this.scratchCleanup = null;
    }

    startDiscountCountdown() {
      this.stopDiscountCountdown();

      const countdown = this.querySelector('[data-discount-countdown]');
      if (!countdown) return;

      const update = () => {
        const now = new Date();
        const midnight = new Date(now);
        midnight.setHours(24, 0, 0, 0);
        const remaining = Math.max(0, midnight.getTime() - now.getTime());
        const totalSeconds = Math.floor(remaining / 1000);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        countdown.textContent = `${hours}:${minutes}:${seconds}`;
        countdown.setAttribute('datetime', `PT${hours}H${minutes}M${seconds}S`);
      };

      update();
      this.discountCountdownTimer = window.setInterval(update, 1000);
    }

    stopDiscountCountdown() {
      if (this.discountCountdownTimer) {
        window.clearInterval(this.discountCountdownTimer);
        this.discountCountdownTimer = null;
      }
    }

    applyDiscount(button) {
      const code = this.discountCode.trim();
      const productUrl = this.dataset.productUrl || '/products/libido';
      const quizResultUrl = `${productUrl}${productUrl.includes('?') ? '&' : '?'}source=quiz_result`;

      if (!code) {
        window.location.assign(quizResultUrl);
        return;
      }

      button.disabled = true;
      this.emitEvent('QuizDiscountApplied');

      const root = window.Shopify?.routes?.root || '/';
      const discountUrl = `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(quizResultUrl)}`;

      // The editor cannot render Shopify's /discount route in its iframe.
      // Navigate to the product exactly like the Menopausia result CTA does.
      if (window.Shopify?.designMode) {
        window.location.assign(quizResultUrl);
        return;
      }

      window.location.assign(discountUrl);
    }

    emitEvent(eventName) {
      this.dispatchEvent(new CustomEvent('inira:quiz-event', {
        bubbles: true,
        detail: { name: eventName, quiz: 'libido' }
      }));
    }

    restart() {
      window.clearTimeout(this.answerTimer);
      this.stopAnalysis();
      this.answers = {};
      this.history = [];
      this.querySelectorAll('[data-answer]').forEach((answer) => {
        answer.classList.remove('is-selected');
        answer.setAttribute('aria-pressed', 'false');
      });
      this.showScreen('intro', { pushHistory: false });
    }
  }

  if (!customElements.get('libido-quiz')) {
    customElements.define('libido-quiz', LibidoQuiz);
  }
})();
